/**
 * B1084 (c) — the `?cart=` HANDOFF (UCP's `continue_url` shape). RULED by
 * the design seat 2026-10-27 19:4xZ under Pathik's standing instruction of
 * 2026-10-06 (class A by recorded default; Pathik overrules with "no on
 * B1084 (c)"). An agent builds a cart — through the storefront's WebMCP
 * tools or the REST wire under a tenant key — and hands a PERSON the link
 * that finishes it in their own browser.
 *
 * The link carries a CART CAPABILITY, never a session:
 *
 *     cho1.<base64url(canonical payload JSON)>.<keyId>.<base64url(signature)>
 *
 * signed with the GUEST-SESSION key (it moves a cart between sessions —
 * the same custody and blast radius as gst1 and mlk1; the version string
 * is inside the signed bytes, so a gst1 verifier refuses cho1 and this
 * parser refuses gst1). Bound to tenant + cartId, 15 minutes, SINGLE USE.
 * Writes never ride the capability: it is spent once, by `adoptCartHandoff`,
 * and every write after that rides the adopting session's own bearer.
 *
 * ADOPTION moves B25d's fence — it does not open it. The person's page mints
 * an ordinary FRESH guest session (the guest door, its rate wall and audit
 * row unchanged), then redeems the capability under it: the cart's creator
 * stamp becomes that session's customer, and every other admission (the
 * builder's session, the builder's API key, an email binding) is removed,
 * so the builder loses the cart. "Never reassigned" (cart.ts's B25c note)
 * holds everywhere else; this is the one door that moves it.
 *
 * Every refusal at redemption is the ONE CartHandoffRefusedError — a spent,
 * expired, forged, foreign and never-minted capability are indistinguishable
 * to the caller (the `why` is for OUR logs).
 *
 * PURITY: signing, verification, randomness and the clock are injected.
 */

import type { Cart, CartStore } from "./cart.js";
import { CartWriteConflictError } from "./cart.js";
import type { Clock } from "./clock.js";
import type { ConsumedClaimReader, MagicLinkConsumedStore } from "./magic-link.js";
import { fromBase64Url, toBase64Url, type TicketSigner, type TicketVerifierPort } from "./ticket-token.js";

/* ---------------------------------------------------------------- types */

export interface CartHandoffPayload {
  readonly tenantId: string;
  readonly channelId: string;
  readonly cartId: string;
  /** The single-use claim's id; consuming it is what spends the capability. */
  readonly handoffId: string;
  readonly issuedAt: number;
  readonly expiresAt: number;
  /** B1084 (d) — the agent that built the cart, so the person's checkout still says channel = agent. */
  readonly agent?: string;
}

export type CartHandoffTokenRefusal = "malformed" | "bad_signature" | "unknown_key" | "expired";

/** The buyer's sentence for every miss (the ruling's own words, widened to cover expiry). */
export const CART_HANDOFF_REFUSAL = "This link was already used or has expired — open your cart from the email or start again.";

/** 15 minutes — the recorded default's own number. */
export const DEFAULT_CART_HANDOFF_TTL_MS = 15 * 60_000;

/** The agent name's cap — @transact/contracts' AGENT_NAME_MAX (pinned equal in the handler test). */
export const CART_HANDOFF_AGENT_MAX = 80;

export class InvalidCartHandoffPayloadError extends Error {
  constructor(why: string) {
    super(`Refusing to mint a cart handoff: ${why}`);
    this.name = "InvalidCartHandoffPayloadError";
  }
}

/** A cart past OPEN carries nothing a person could finish — the mint refuses. */
export class CartHandoffClosedError extends Error {
  constructor(
    readonly tenantId: string,
    readonly cartId: string,
  ) {
    super(`Cart ${cartId} is checked out and cannot be handed off`);
    this.name = "CartHandoffClosedError";
  }
}

/** The redemption said no. ONE wire shape for every reason. */
export class CartHandoffRefusedError extends Error {
  constructor(readonly why: CartHandoffTokenRefusal | "consumed" | "tenant_mismatch" | "no_cart" | "closed" | "unwired") {
    super(CART_HANDOFF_REFUSAL);
    this.name = "CartHandoffRefusedError";
  }
}

export interface CartHandoffDeps {
  readonly carts: Pick<CartStore, "get" | "update">;
  /** The single-use claims — the magic link's own atomic store (ids are `cho-` prefixed, a namespace of their own). */
  readonly consumed: MagicLinkConsumedStore;
  /** The GUEST-SESSION signing key. */
  readonly signer: TicketSigner;
  /** Verification for the redemption; absent = every redemption refuses (fail closed). */
  readonly verifier?: TicketVerifierPort;
  readonly clock: Clock;
  /** Fresh, unguessable id — crypto randomness is I/O, minted at the edge. */
  readonly newHandoffId: () => string;
  /** The storefront base a continue_url lands on (`<base>/checkout?tenant=…&cart=…`); absent = no URL, the token alone. */
  readonly continueUrlBase?: string;
  readonly ttlMs?: number;
}

/* -------------------------------------------------------------- payload */

const PAYLOAD_KEYS = ["tenantId", "channelId", "cartId", "handoffId", "issuedAt", "expiresAt", "agent"] as const;
const REQUIRED_STRINGS = ["tenantId", "channelId", "cartId", "handoffId"] as const;
const VERSION = "cho1";
const encoder = new TextEncoder();
const decoder = new TextDecoder();

function agentProblem(agent: unknown): string | undefined {
  if (agent === undefined) return undefined;
  if (typeof agent !== "string" || agent.trim().length === 0) return "agent must be a non-empty string";
  if (agent.length > CART_HANDOFF_AGENT_MAX) return `agent runs past ${CART_HANDOFF_AGENT_MAX} characters`;
  return undefined;
}

function assertPayload(payload: CartHandoffPayload): void {
  for (const field of REQUIRED_STRINGS) {
    const v = payload[field];
    if (typeof v !== "string" || v.trim().length === 0) throw new InvalidCartHandoffPayloadError(`${field} is required`);
  }
  for (const field of ["issuedAt", "expiresAt"] as const) {
    const v = payload[field];
    if (!Number.isSafeInteger(v) || v < 0) throw new InvalidCartHandoffPayloadError(`${field} must be a non-negative epoch-ms integer`);
  }
  if (payload.expiresAt <= payload.issuedAt) throw new InvalidCartHandoffPayloadError("expiresAt must follow issuedAt");
  const agent = agentProblem(payload.agent);
  if (agent !== undefined) throw new InvalidCartHandoffPayloadError(agent);
}

/** Canonical form: fixed key order, an absent agent absent — deterministic bytes. */
function canonicalPayloadJson(payload: CartHandoffPayload): string {
  const ordered: Record<string, unknown> = {};
  for (const key of PAYLOAD_KEYS) if (payload[key] !== undefined) ordered[key] = payload[key];
  return JSON.stringify(ordered);
}

function parsePayload(json: string): CartHandoffPayload | undefined {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!(PAYLOAD_KEYS as readonly string[]).includes(key)) return undefined; // extras are malformed
  }
  const out: Record<string, unknown> = {};
  for (const field of REQUIRED_STRINGS) {
    const v = record[field];
    if (typeof v !== "string" || v.trim().length === 0) return undefined;
    out[field] = v;
  }
  for (const field of ["issuedAt", "expiresAt"] as const) {
    const v = record[field];
    if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) return undefined;
    out[field] = v;
  }
  if (record["agent"] !== undefined) {
    if (agentProblem(record["agent"]) !== undefined) return undefined;
    out["agent"] = record["agent"];
  }
  const payload = out as unknown as CartHandoffPayload;
  if (payload.expiresAt <= payload.issuedAt) return undefined;
  return payload;
}

/* ------------------------------------------------------- token mechanics */

export async function mintCartHandoffToken(payload: CartHandoffPayload, signer: TicketSigner): Promise<string> {
  assertPayload(payload);
  if (signer.keyId.trim().length === 0 || signer.keyId.includes("."))
    throw new InvalidCartHandoffPayloadError("signer keyId must be non-empty and dot-free");
  const body = toBase64Url(encoder.encode(canonicalPayloadJson(payload)));
  const signature = await signer.sign(encoder.encode(`${VERSION}.${body}.${signer.keyId}`));
  return `${VERSION}.${body}.${signer.keyId}.${toBase64Url(signature)}`;
}

/** Structural parse — fail-closed, throw-free, canonical-form-only. */
export function parseCartHandoffToken(token: string):
  | { readonly ok: true; readonly payload: CartHandoffPayload; readonly keyId: string; readonly signedBytes: Uint8Array; readonly signature: Uint8Array }
  | { readonly ok: false; readonly why: "malformed" } {
  const malformed = { ok: false as const, why: "malformed" as const };
  const parts = token.split(".");
  if (parts.length !== 4) return malformed;
  const [version, body, keyId, sig] = parts as [string, string, string, string];
  if (version !== VERSION || body.length === 0 || keyId.length === 0 || sig.length === 0) return malformed;
  const payloadBytes = fromBase64Url(body);
  const signature = fromBase64Url(sig);
  if (payloadBytes === undefined || signature === undefined || signature.length === 0) return malformed;
  const payload = parsePayload(decoder.decode(payloadBytes));
  if (payload === undefined) return malformed;
  if (toBase64Url(encoder.encode(canonicalPayloadJson(payload))) !== body) return malformed;
  return { ok: true, payload, keyId, signedBytes: encoder.encode(`${VERSION}.${body}.${keyId}`), signature };
}

/** The person's link: the storefront's checkout door, tenant-addressed (B830), the capability in `cart`. */
export function cartHandoffUrl(base: string, tenantId: string, token: string): string {
  return `${base.replace(/\/+$/, "")}/checkout?tenant=${encodeURIComponent(tenantId)}&cart=${token}`;
}

/* ---------------------------------------------------------- orchestration */

export interface MintCartHandoffCommand {
  readonly tenantId: string;
  /** The cart, ALREADY fenced to the caller by the handler (B25d). */
  readonly cart: Cart;
  readonly agent?: string;
}

export interface MintedCartHandoff {
  readonly token: string;
  readonly expiresAt: number;
  readonly continueUrl?: string;
}

/** Mint the capability for an OPEN cart the caller owns. Writes nothing: the claim exists only once it is spent. */
export async function mintCartHandoff(deps: CartHandoffDeps, cmd: MintCartHandoffCommand): Promise<MintedCartHandoff> {
  if (cmd.cart.tenantId !== cmd.tenantId) throw new InvalidCartHandoffPayloadError("the cart belongs to another tenant");
  if (cmd.cart.status !== "OPEN") throw new CartHandoffClosedError(cmd.tenantId, cmd.cart.cartId);
  const now = deps.clock.now();
  const expiresAt = now + (deps.ttlMs ?? DEFAULT_CART_HANDOFF_TTL_MS);
  const token = await mintCartHandoffToken(
    {
      tenantId: cmd.tenantId,
      channelId: cmd.cart.channelId,
      cartId: cmd.cart.cartId,
      handoffId: `cho-${deps.newHandoffId()}`,
      issuedAt: now,
      expiresAt,
      ...(cmd.agent === undefined ? {} : { agent: cmd.agent }),
    },
    deps.signer,
  );
  return {
    token,
    expiresAt,
    ...(deps.continueUrlBase === undefined ? {} : { continueUrl: cartHandoffUrl(deps.continueUrlBase, cmd.tenantId, token) }),
  };
}

export interface AdoptCartHandoffCommand {
  readonly tenantId: string;
  readonly token: string;
  /** The adopting session's customer id (a fresh guest, or a signed-in buyer). */
  readonly adopterCustomerId: string;
}

export interface AdoptedCart {
  readonly cartId: string;
  readonly agent?: string;
}

/**
 * The redemption: verify (structure, signature, expiry), the tenant claim,
 * SPEND the single use atomically (losers of a double-open race refuse),
 * then move the fence on an OPEN cart. A cart checked out since the mint
 * stays exactly as it is.
 */
export async function adoptCartHandoff(deps: CartHandoffDeps, cmd: AdoptCartHandoffCommand): Promise<AdoptedCart> {
  if (deps.verifier === undefined) throw new CartHandoffRefusedError("unwired");
  const parsed = parseCartHandoffToken(cmd.token);
  if (!parsed.ok) throw new CartHandoffRefusedError(parsed.why);
  const verdict = await deps.verifier.verify(parsed.signedBytes, parsed.signature, parsed.keyId);
  if (verdict !== "ok") throw new CartHandoffRefusedError(verdict);
  const payload = parsed.payload;
  const now = deps.clock.now();
  if (now >= payload.expiresAt) throw new CartHandoffRefusedError("expired");
  if (payload.tenantId !== cmd.tenantId) throw new CartHandoffRefusedError("tenant_mismatch");

  const fresh = await deps.consumed.consume(payload.tenantId, payload.handoffId, now);
  if (!fresh) throw new CartHandoffRefusedError("consumed");

  // One re-read on a concurrent write (a line the builder changed a moment ago).
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const row = await deps.carts.get(payload.tenantId, payload.cartId);
    if (row === undefined) throw new CartHandoffRefusedError("no_cart");
    if (row.cart.status !== "OPEN") throw new CartHandoffRefusedError("closed");
    const { createdByApiKeyId: _key, customerId: _bound, ...rest } = row.cart;
    const moved: Cart = { ...rest, createdByCustomerId: cmd.adopterCustomerId, updatedAt: Math.max(now, row.cart.updatedAt) };
    try {
      await deps.carts.update(moved, row.version);
      return { cartId: payload.cartId, ...(payload.agent === undefined ? {} : { agent: payload.agent }) };
    } catch (err) {
      if (!(err instanceof CartWriteConflictError) || attempt === 1) throw err;
    }
  }
  /* c8 ignore next */
  throw new CartHandoffRefusedError("no_cart");
}

/* ------------------------------------------------------------- the peek */

export interface PeekCartHandoffDeps {
  readonly carts: Pick<CartStore, "get">;
  readonly spent: ConsumedClaimReader;
  /** Absent = every peek refuses (fail closed), as adoption does. */
  readonly verifier?: TicketVerifierPort;
  readonly clock: Clock;
}

export interface PeekedCartHandoff {
  readonly payload: CartHandoffPayload;
  readonly cart: Cart;
}

/**
 * B1085 (4b) — the UCP checkout id IS the cart capability (design-seat
 * ruling 2026-10-28 13:4xZ), and `get_checkout` reads its cart READ-ONLY:
 * the same verification adoption runs (structure, signature, expiry, tenant)
 * but nothing is spent and nothing moves. It refuses once the capability's
 * single use is spent (a person adopted the cart), at expiry, and for a cart
 * no longer OPEN — every miss the one CartHandoffRefusedError, as adoption.
 */
export async function peekCartHandoff(deps: PeekCartHandoffDeps, cmd: { readonly tenantId: string; readonly token: string }): Promise<PeekedCartHandoff> {
  if (deps.verifier === undefined) throw new CartHandoffRefusedError("unwired");
  const parsed = parseCartHandoffToken(cmd.token);
  if (!parsed.ok) throw new CartHandoffRefusedError(parsed.why);
  const verdict = await deps.verifier.verify(parsed.signedBytes, parsed.signature, parsed.keyId);
  if (verdict !== "ok") throw new CartHandoffRefusedError(verdict);
  const payload = parsed.payload;
  if (deps.clock.now() >= payload.expiresAt) throw new CartHandoffRefusedError("expired");
  if (payload.tenantId !== cmd.tenantId) throw new CartHandoffRefusedError("tenant_mismatch");
  if (await deps.spent.spent(payload.tenantId, payload.handoffId)) throw new CartHandoffRefusedError("consumed");
  const row = await deps.carts.get(payload.tenantId, payload.cartId);
  if (row === undefined) throw new CartHandoffRefusedError("no_cart");
  if (row.cart.status !== "OPEN") throw new CartHandoffRefusedError("closed");
  return { payload, cart: row.cart };
}
